import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';

import palette from '../../styles/palette';
import RightDrawerModal from '../../RightDrawerModal';
import ModalTable from '../../common/Table/ModalTable';
import {
  AFFECTED_STOPS,
  AVAILABLE_ROUTES,
  CONTINUE_SPLIT_WARNING,
  MOVE_AND_OPTIMISE,
  MOVE_AND_SCHEDULE,
  MOVE_ONLY,
  NO_PARCELS_MATCH_CRITERIA,
  REMOVE,
  REMOVE_OPTIONS,
  REMOVE_PARCELS_WARNING,
  SELECT_PLACEHOLDER,
  TRANSFER_HIGH_VALUE_PARCEL,
} from '../../../constants/strings';
import SelectDriverRoute from '../DriverRequests/components/SelectDriverRoute';
import useLocationQueryParams from '../../../hooks/useLocationQueryParams';
import { getCurrentUserId } from '../../../store/selectors/user';
import { getAvailableRoutesTransfer } from '../../../store/actions/request';
import {
  bodyCellStyles,
  headerCellStyles,
} from '../../../features/request/tableStyles';

import {
  getRouteStops,
  isRouteReadyOrOpen,
} from '../../../store/selectors/route';

import { useOverlay } from '../../Overlay';

import { isRouteLocked } from '../TransferTo/utils';
import SliderCardAutocomplete from '../../SliderCardAutocomplete';
import { REMOVE_OPTIONS_LIST } from './removeOptions';
import TransferActionButtons from '../TransferTo/components/TransferActionButtons';
import { getUniqueRoutesFromSelectedStops } from '../../../models/routeDetail';
import affectedStopsTableProps from './affectedStopsTableProps';
import { hasHighValueParcel } from '../../../models/parcel';
import StaticBanner from '../../StaticBanner';
import { Text } from '../../Base';
import useSelectRouteHandler from '../DriverRequests/useSelectRouteHandler';
import StaticBannerWithConfirmActions from '../../StaticBanner/StaticBannerWithConfirmActions';

const useStyles = makeStyles(({ typography }) => ({
  root: {
    margin: '40px 125px',
    borderRadius: 10,
  },
  message: {
    padding: '15px 25px',
    color: palette.typography.black.high,
    fontSize: typography.pxToRem(16),
    fontWeight: 500,
    lineHeight: typography.pxToRem(20),
    letterSpacing: typography.pxToRem(-0.32),
    textTransform: 'capitalize',
  },
  overlay: {
    display: 'flex',
    backgroundColor: palette.typography.black.inactive,
    position: 'absolute',
    zIndex: 9999,
    top: '19px',
    bottom: 0,
    right: 0,
    left: 0,
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
  },
  moveUp: {
    marginTop: '-40px',
  },
  content: {
    position: 'relative',
    flex: 1,
  },
}));

const RemoveParcels = ({
  open,
  onClose,
  onConfirm,
  onMove,
  getAffectedStops,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { showOverlayWhile } = useOverlay();

  const [selectedRemoveOption, setRemoveOption] = useState(null);
  const [affectedStops, setAffectedStops] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [showRemoveSplitMessage, setShowRemoveSplitMessage] = useState(false);

  const [selectedRoute, onSelectRoute, setAvailableRoutes, setSelectedRoute] =
    useSelectRouteHandler();

  const query = useLocationQueryParams();
  const uid = useSelector(getCurrentUserId);
  const routeReadyOrOpen = useSelector(isRouteReadyOrOpen);
  const stops = useSelector(getRouteStops);
  const { depot, date } = query;

  const isActionBtnDisabled =
    !selectedRoute ||
    isRouteLocked(routes, selectedRoute) ||
    !affectedStops.length;

  const getStops = useCallback(
    async (selectedRemoveOption) => {
      if (selectedRemoveOption) {
        return await getAffectedStops(selectedRemoveOption);
      }
      return [];
    },
    [getAffectedStops]
  );

  useEffect(() => {
    const fetchRoutes = async () => {
      const availableRoutes = await dispatch(
        getAvailableRoutesTransfer(
          depot,
          date,
          uid,
          getUniqueRoutesFromSelectedStops(affectedStops)
        )
      );
      setRoutes(availableRoutes);
      setAvailableRoutes(availableRoutes);
    };
    selectedRemoveOption && fetchRoutes();
  }, [
    date,
    depot,
    uid,
    selectedRemoveOption,
    affectedStops,
    dispatch,
    setAvailableRoutes,
  ]);

  const onMoveParcels = useCallback(
    async (optimise, transferOnly) => {
      // TODO add action ID
      const success = await onMove(selectedRoute, selectedRemoveOption);
      success && onConfirm({ optimise, transferOnly });
    },
    [selectedRoute, selectedRemoveOption, onMove, onConfirm]
  );

  const getTableProps = useCallback(
    () =>
      affectedStopsTableProps(
        headerCellStyles(),
        bodyCellStyles(),
        affectedStops
      ),
    [affectedStops]
  );

  const tableProps = getTableProps();

  const showHighValueMessage = useMemo(
    () => hasHighValueParcel(tableProps.data),
    [tableProps.data]
  );

  const onChange = useCallback(
    async (value) => {
      onSelectRoute(value);
    },
    [onSelectRoute]
  );

  const onRemoveOptionsChange = useCallback(
    async (value) => {
      const removeOption =
        value && REMOVE_OPTIONS_LIST.find((option) => option.value === value);
      const stops = await getStops(removeOption);
      setAffectedStops(stops);
      setRemoveOption(removeOption);
      setSelectedRoute(null);
      // TODO add action id
    },
    [getStops, setSelectedRoute]
  );

  const processedMoveAction = useCallback(
    (optimise, transferOnly) =>
      showOverlayWhile(async () => await onMoveParcels(optimise, transferOnly)),
    [onMoveParcels, showOverlayWhile]
  );

  const removeButtonsConfig = useMemo(
    () => ({
      move: {
        isDisabled: !routeReadyOrOpen || isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.orange,
          border: palette.modalButtons.greenBorder,
        },
        title: MOVE_ONLY,
        onClick: processedMoveAction(false, true),
      },
      optimise: {
        isDisabled: !routeReadyOrOpen || isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.green,
          border: palette.modalButtons.greenBorder,
        },
        title: MOVE_AND_OPTIMISE,
        onClick: processedMoveAction(true, false),
      },
      schedule: {
        isDisabled: isActionBtnDisabled,
        color: {
          bg: palette.modalButtons.blue,
          border: palette.btn.blueBorder,
        },
        title: MOVE_AND_SCHEDULE,
        onClick: processedMoveAction(false, false),
      },
    }),
    [routeReadyOrOpen, isActionBtnDisabled, processedMoveAction]
  );

  useEffect(() => {
    if (affectedStops.length) {
      const isPartialTransfer = !!affectedStops.find((affectedStop) => {
        const originalStop = stops.find(
          ({ stopId: id }) => id === affectedStop.stopId
        );
        return originalStop.parcel.length !== affectedStop.parcel.length;
      });
      return setShowRemoveSplitMessage(isPartialTransfer);
    }
  }, [affectedStops]);

  return (
    <RightDrawerModal
      open={open}
      onClose={onClose}
      title={REMOVE}
      actions={<TransferActionButtons config={removeButtonsConfig} />}
    >
      <div className={classes.container}>
        <div className={classes.messages}>
          {showHighValueMessage && (
            <StaticBanner message={TRANSFER_HIGH_VALUE_PARCEL} />
          )}
          {showRemoveSplitMessage && (
            <StaticBannerWithConfirmActions
              onContinue={() => setShowRemoveSplitMessage(false)}
              onCancel={onClose}
              message={`${REMOVE_PARCELS_WARNING} ${CONTINUE_SPLIT_WARNING}`}
            />
          )}
        </div>
        <div
          className={clsx(
            classes.content,
            showRemoveSplitMessage && classes.moveUp
          )}
        >
          {showRemoveSplitMessage && <div className={classes.overlay} />}
          <SliderCardAutocomplete
            options={REMOVE_OPTIONS_LIST}
            onChange={onRemoveOptionsChange}
            value={selectedRemoveOption?.value || null}
            sliderTitle={REMOVE_OPTIONS}
            inputLabel={REMOVE_OPTIONS}
            disabled={showRemoveSplitMessage}
          />
          {selectedRemoveOption && !affectedStops.length && (
            <Paper className={classes.root} elevation={5}>
              <Text className={classes.message} component="h6">
                {NO_PARCELS_MATCH_CRITERIA}
              </Text>
            </Paper>
          )}
          {selectedRemoveOption && !!affectedStops.length && (
            <>
              <ModalTable title={AFFECTED_STOPS} {...tableProps} />
              <SelectDriverRoute
                options={routes}
                onChange={onChange}
                value={selectedRoute}
                title={AVAILABLE_ROUTES}
                label={null}
                placeholder={SELECT_PLACEHOLDER}
                disabled={showRemoveSplitMessage}
              />
            </>
          )}
        </div>
      </div>
    </RightDrawerModal>
  );
};

export default memo(RemoveParcels);
